import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Crown, TrendingUp, TrendingDown, BarChart3, PieChart, Globe,
  Users, Wallet, Zap, Shield, FileText, BookOpen, ArrowRight
} from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const PublicPortal = () => {
  const [economicData, setEconomicData] = useState(null);
  const [cbdcStats, setCbdcStats] = useState(null);
  const [stabilityData, setStabilityData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [economicRes, cbdcRes, stabilityRes] = await Promise.all([
        axios.get(`${API}/public/economic-indicators`),
        axios.get(`${API}/public/cbdc-stats`),
        axios.get(`${API}/public/financial-stability`)
      ]);
      setEconomicData(economicRes.data);
      setCbdcStats(cbdcRes.data);
      setStabilityData(stabilityRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mock historical data for charts
  const gdpHistory = [
    { quarter: 'Q1 23', value: 2.1 },
    { quarter: 'Q2 23', value: 2.4 },
    { quarter: 'Q3 23', value: 2.8 },
    { quarter: 'Q4 23', value: 2.6 },
    { quarter: 'Q1 24', value: 3.0 },
    { quarter: 'Q2 24', value: 3.2 }
  ];

  const inflationHistory = [
    { month: 'Jul', value: 2.8 },
    { month: 'Aug', value: 2.6 },
    { month: 'Sep', value: 2.4 },
    { month: 'Oct', value: 2.3 },
    { month: 'Nov', value: 2.2 },
    { month: 'Dec', value: 2.1 }
  ];

  const cbdcAdoption = [
    { month: 'Jan', wallets: 1.8 },
    { month: 'Feb', wallets: 1.9 },
    { month: 'Mar', wallets: 2.1 },
    { month: 'Apr', wallets: 2.2 },
    { month: 'May', wallets: 2.4 },
    { month: 'Jun', wallets: 2.5 }
  ];

  const financialLiteracy = [
    { title: 'Understanding CBDC', description: 'Learn about Central Bank Digital Currency and how it works', icon: <Wallet /> },
    { title: 'Digital Payments Guide', description: 'How to make secure instant payments using the national system', icon: <Zap /> },
    { title: 'Investment Basics', description: 'Introduction to sovereign bonds and wealth management', icon: <TrendingUp /> },
    { title: 'Security Best Practices', description: 'Protect your digital wallet and financial data', icon: <Shield /> }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-portal">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
                <Crown className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="font-serif text-lg font-semibold text-primary">Royal CBDC</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/bonds">
                <Button variant="ghost">Bond Market</Button>
              </Link>
              <Link to="/login">
                <Button data-testid="public-login-btn">Access Portal</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">
              Public Information Portal
            </p>
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-primary tracking-tight mb-6">
              Transparency in<br />
              <span className="font-semibold">Sovereign Finance</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Access real-time economic indicators, CBDC statistics, and financial stability reports.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="economic" className="space-y-8">
          <TabsList className="bg-muted/50 mx-auto flex w-fit">
            <TabsTrigger value="economic" data-testid="tab-economic">Economic Data</TabsTrigger>
            <TabsTrigger value="cbdc" data-testid="tab-cbdc">CBDC Statistics</TabsTrigger>
            <TabsTrigger value="stability" data-testid="tab-stability">Financial Stability</TabsTrigger>
            <TabsTrigger value="literacy" data-testid="tab-literacy">Resources</TabsTrigger>
          </TabsList>

          {/* Economic Data Tab */}
          <TabsContent value="economic" className="space-y-8">
            {/* Key Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {economicData && [
                { 
                  label: 'GDP Growth', 
                  value: `${economicData.gdp_growth.current}%`, 
                  change: economicData.gdp_growth.current - economicData.gdp_growth.previous,
                  icon: <BarChart3 className="h-5 w-5" />
                },
                { 
                  label: 'Inflation Rate', 
                  value: `${economicData.inflation_rate.current}%`, 
                  change: economicData.inflation_rate.previous - economicData.inflation_rate.current,
                  target: `Target: ${economicData.inflation_rate.target}%`,
                  icon: <TrendingUp className="h-5 w-5" />
                },
                { 
                  label: 'Interest Rate', 
                  value: `${economicData.interest_rate.current}%`, 
                  change: economicData.interest_rate.current - economicData.interest_rate.previous,
                  icon: <PieChart className="h-5 w-5" />
                },
                { 
                  label: 'Foreign Reserves', 
                  value: `$${economicData.foreign_reserves.current}B`, 
                  icon: <Globe className="h-5 w-5" />
                }
              ].map((indicator, idx) => (
                <Card key={idx} className="sovereign-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-muted-foreground">{indicator.label}</span>
                      <div className="text-muted-foreground">{indicator.icon}</div>
                    </div>
                    <p className="font-mono text-3xl font-semibold" data-testid={`indicator-${idx}`}>
                      {indicator.value}
                    </p>
                    {indicator.change !== undefined && (
                      <p className={`text-sm mt-2 flex items-center gap-1 ${
                        indicator.change >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {indicator.change >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                        {Math.abs(indicator.change).toFixed(1)}% from previous
                      </p>
                    )}
                    {indicator.target && (
                      <p className="text-xs text-muted-foreground mt-1">{indicator.target}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="sovereign-card">
                <CardHeader>
                  <CardTitle className="font-sans text-lg">GDP Growth Trend</CardTitle>
                  <CardDescription>Quarterly GDP growth rate (%)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={gdpHistory}>
                        <defs>
                          <linearGradient id="colorGdp" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0047AB" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#0047AB" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="quarter" axisLine={false} tickLine={false} />
                        <YAxis axisLine={false} tickLine={false} domain={[0, 'auto']} />
                        <Tooltip />
                        <Area type="monotone" dataKey="value" stroke="#0047AB" fill="url(#colorGdp)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="sovereign-card">
                <CardHeader>
                  <CardTitle className="font-sans text-lg">Inflation Trend</CardTitle>
                  <CardDescription>Monthly inflation rate (%)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={inflationHistory}>
                        <XAxis dataKey="month" axisLine={false} tickLine={false} />
                        <YAxis axisLine={false} tickLine={false} domain={[0, 'auto']} />
                        <Tooltip />
                        <Line type="monotone" dataKey="value" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* CBDC Statistics Tab */}
          <TabsContent value="cbdc" className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {cbdcStats && [
                { label: 'Total Wallets', value: (cbdcStats.total_wallets / 1000000).toFixed(1) + 'M', icon: <Wallet /> },
                { label: 'Active (24h)', value: (cbdcStats.active_wallets_24h / 1000000).toFixed(1) + 'M', icon: <Users /> },
                { label: 'Transactions (24h)', value: (cbdcStats.transactions_24h / 1000000).toFixed(1) + 'M', icon: <Zap /> },
                { label: 'Avg Settlement', value: `${cbdcStats.settlement_time_avg_seconds}s`, icon: <TrendingUp /> }
              ].map((stat, idx) => (
                <Card key={idx} className="dashboard-card">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">{stat.label}</span>
                      <div className="text-muted-foreground h-5 w-5">{stat.icon}</div>
                    </div>
                    <p className="font-mono text-2xl font-semibold" data-testid={`cbdc-stat-${idx}`}>{stat.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="sovereign-card">
                <CardHeader>
                  <CardTitle className="font-sans text-lg">Wallet Adoption</CardTitle>
                  <CardDescription>Monthly growth in millions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={cbdcAdoption}>
                        <XAxis dataKey="month" axisLine={false} tickLine={false} />
                        <YAxis axisLine={false} tickLine={false} />
                        <Tooltip />
                        <Bar dataKey="wallets" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="sovereign-card">
                <CardHeader>
                  <CardTitle className="font-sans text-lg">Adoption Rates</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {cbdcStats && [
                      { label: 'Retail Adoption', value: cbdcStats.retail_adoption_rate },
                      { label: 'Merchant Adoption', value: cbdcStats.merchant_adoption_rate }
                    ].map((item, idx) => (
                      <div key={idx} className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">{item.label}</span>
                          <span className="font-mono font-semibold">{item.value}%</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full transition-all duration-500"
                            style={{ width: `${item.value}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Financial Stability Tab */}
          <TabsContent value="stability" className="space-y-8">
            {stabilityData && (
              <>
                <Card className="sovereign-card bg-primary text-primary-foreground">
                  <CardContent className="p-8 text-center">
                    <p className="text-sm uppercase tracking-wider text-primary-foreground/70 mb-2">
                      Banking Sector Health
                    </p>
                    <p className="font-serif text-4xl md:text-5xl font-light capitalize" data-testid="stability-status">
                      {stabilityData.banking_sector_health}
                    </p>
                    <p className="text-sm text-primary-foreground/70 mt-4">
                      Last Assessment: {stabilityData.last_assessment_date}
                    </p>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Capital Adequacy', value: `${stabilityData.capital_adequacy_ratio}%`, status: 'good' },
                    { label: 'Liquidity Coverage', value: `${stabilityData.liquidity_coverage_ratio}%`, status: 'good' },
                    { label: 'NPL Ratio', value: `${stabilityData.non_performing_loans_ratio}%`, status: 'good' },
                    { label: 'Systemic Risk', value: stabilityData.systemic_risk_index.toFixed(2), status: 'low' }
                  ].map((metric, idx) => (
                    <Card key={idx} className="dashboard-card">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-muted-foreground mb-2">{metric.label}</p>
                        <p className="font-mono text-2xl font-semibold" data-testid={`stability-metric-${idx}`}>
                          {metric.value}
                        </p>
                        <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium uppercase status-active">
                          {metric.status}
                        </span>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <Card className="sovereign-card">
                  <CardHeader>
                    <CardTitle className="font-sans text-lg">Stress Test Results</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <div className="w-24 h-24 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                          <Shield className="h-12 w-12 text-green-500" />
                        </div>
                        <p className="font-serif text-2xl font-semibold text-green-600 uppercase">
                          {stabilityData.stress_test_results}
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">
                          All scenarios within acceptable parameters
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Financial Literacy Tab */}
          <TabsContent value="literacy" className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="font-serif text-2xl md:text-3xl font-normal text-primary">
                Financial Education Resources
              </h2>
              <p className="text-muted-foreground mt-2">
                Learn about digital finance and make informed decisions
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {financialLiteracy.map((resource, idx) => (
                <Card key={idx} className="sovereign-card group cursor-pointer" data-testid={`resource-${idx}`}>
                  <CardContent className="p-6 flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/5 flex items-center justify-center shrink-0 group-hover:bg-primary/10 transition-colors">
                      <div className="text-primary h-6 w-6">{resource.icon}</div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">{resource.title}</h3>
                      <p className="text-sm text-muted-foreground">{resource.description}</p>
                      <p className="text-sm text-primary mt-3 flex items-center gap-1 group-hover:gap-2 transition-all">
                        Learn more <ArrowRight className="h-4 w-4" />
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="sovereign-card">
              <CardHeader>
                <CardTitle className="font-sans text-lg flex items-center gap-2">
                  <BookOpen className="h-5 w-5" /> Policy Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { title: 'Monetary Policy Framework', date: 'Updated Jan 2024' },
                    { title: 'CBDC Regulations', date: 'Updated Dec 2023' },
                    { title: 'AML/CFT Guidelines', date: 'Updated Nov 2023' },
                    { title: 'Payment Systems Regulations', date: 'Updated Oct 2023' }
                  ].map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <span className="font-medium text-sm">{doc.title}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{doc.date}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            © 2024 Royal CBDC. All data is for demonstration purposes.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PublicPortal;
